import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { JobRequestOverviewDto } from '@models/job-request-overview-dto.interface';
import { JobRequestDesignSummaryDto } from '@models/job-request-summary-dto.interface';
import { EditJobRequestDto } from '@models/edit-job-request-dto.interface';
import { SaveJobRequestDto } from '@models/save-job-request-dto.interface';
import { CreateMudCheckDto } from '@models/create-mud-check-dto.interface';
import { TestTypeLookupDto } from '@models/test-type-lookup.interface';
import { JobRequestReportDto } from '@models/job-request-report-dto.interface';
import { JobRequestShippingInformationDto, ShipmentRecordDto } from '@models/shipping-information-dto.interface';


@Injectable({ providedIn: 'root' })
export class JobRequestsService {
  private readonly api = inject(ApiService);

  getForCreate(jobId: number): Observable<EditJobRequestDto> {
    return this.api.get(`jobrequests/new?jobId=${jobId}`);
  }

  getForEdit(id: number): Observable<EditJobRequestDto> {
    return this.api.get(`jobrequests/${id}/edit`);
  }

  createPilotTest(dto: SaveJobRequestDto): Observable<number> {
    return this.api.post('jobrequests/pilot', dto as unknown as object);
  }

  createCutbackWeighUp(dto: SaveJobRequestDto): Observable<number> {
    return this.api.post('jobrequests/cutback-weighup', dto as unknown as object);
  }

  updateJobRequest(id: number, dto: SaveJobRequestDto): Observable<void> {
    return this.api.put(`jobrequests/${id}`, dto);
  }

  updateMudCheckConstants(
    id: number,
    dto: {
      bariteSgconst: number | null;
      baseFluidSgconst: number | null;
      lowGravitySolidsSgconst: number | null;
    }
  ): Observable<void> {
    return this.api.put(`jobrequests/${id}/mud-check-constants`, dto);
  }

  deleteJobRequest(id: number): Observable<void> {
    return this.api.post(`jobrequests/${id}/delete`, {});
  }

  lockJobRequest(id: number): Observable<void> {
    return this.api.post(`jobrequests/${id}/lock`, {});
  }

  unlockJobRequest(id: number): Observable<void> {
    return this.api.post(`jobrequests/${id}/unlock`, {});
  }

  getReport(id: number): Observable<JobRequestReportDto> {
    return this.api.get(`jobrequests/${id}/report`);
  }

  shipJobRequest(id: number): Observable<void> {
    return this.api.post(`jobrequests/${id}/ship`, {});
  }

  getShippingInformation(id: number): Observable<JobRequestShippingInformationDto> {
    return this.api.get(`jobrequests/${id}/shipping-information`);
  }

  updateShippingInformation(id: number, shipmentRecords: ShipmentRecordDto[]): Observable<void> {
    return this.api.put(`jobrequests/${id}/shipment-records`, shipmentRecords as unknown as object);
  }

  markReportSent(id: number, isReportSent: boolean): Observable<void> {
    return this.api.post(`jobrequests/${id}/report-sent`, { isReportSent });
  }

  getWorksheetUrl(id: number): Observable<{ url: string | null }> {
    return this.api.get(`jobrequests/${id}/worksheet`);
  }

  getOverview(id: number): Observable<JobRequestOverviewDto> {
    return this.api.get(`jobrequests/${id}/overview`);
  }

  getSummary(id: number): Observable<JobRequestDesignSummaryDto> {
    return this.api.get(`jobrequests/${id}/summary`);
  }

  addTestGroup(id: number, name?: string): Observable<{ testGroupId: number }> {
    return this.api.post(`jobrequests/${id}/testgroups`, { name: name ?? null });
  }

  addTestToGroup(
    jobRequestId: number,
    testGroupId: number,
    testTypeId: number,
    readings?: { temperature: number; pressure: number }[]
  ): Observable<{ testInputId: number }> {
    return this.api.post(`jobrequests/${jobRequestId}/testgroups/${testGroupId}/tests`, { testTypeId, readings: readings ?? null });
  }

  renameTestGroup(jobRequestId: number, testGroupId: number, name: string): Observable<void> {
    return this.api.put(`jobrequests/${jobRequestId}/testgroups/${testGroupId}`, { name });
  }

  deleteTestGroup(jobRequestId: number, testGroupId: number): Observable<void> {
    return this.api.delete(`jobrequests/${jobRequestId}/testgroups/${testGroupId}`);
  }

  getMudCheckTestTypes(): Observable<TestTypeLookupDto[]> {
    return this.api.get('jobs/mudcheck/test-types');
  }

  createMudCheck(jobId: number, dto: CreateMudCheckDto): Observable<{ jobRequestId: number }> {
    return this.api.post(`jobs/${jobId}/mudcheck`, dto);
  }
}
